<template>
    <div>
        <v-progress-linear v-if="loading" indeterminate color="primary"/>
        <div class="ma-6 mb-2 text-h5 kpmgPrimary-900">{{ $t('appUser.title') }}</div>

        <div v-if="!loading" class="mx-6">
            <v-toolbar flat color="white" class="mb-2">
                <v-btn color="primary" prepend-icon="mdi-plus" variant="elevated"
                       @click="openCreate">
                    {{ $t('appUser.createBtnTitle') }}
                </v-btn>
                <v-spacer/>
                <v-text-field
                    v-model="search"
                    prepend-icon="mdi-magnify"
                    variant="underlined"
                    :placeholder="$t('appUser.search')"
                    hide-details
                    class="ms-4"
                    style="max-width: 300px"
                />
            </v-toolbar>

            <v-data-table
                v-model:expanded="expanded"
                :headers="userHeaders"
                :items="filteredUsers"
                item-value="user_id"
                show-expand
                class="elevation-0"
            >
                <template #item.username="{ item }">
                    {{ item.username }}
                </template>
                <template #item.display_name="{ item }">
                    {{ fullName(item) }}
                </template>
                <template #item.mappings="{ item }">
                    <v-chip size="small" color="primary" variant="tonal">
                        {{ item.mappings.length }}
                    </v-chip>
                </template>

                <template #expanded-row="{ columns, item }">
                    <tr>
                        <td :colspan="columns.length" class="pa-0">
                            <v-table density="compact" class="bg-grey-lighten-5 ma-2 rounded">
                                <thead>
                                    <tr class="text-caption">
                                        <th>{{ $t('appUser.table.app_id') }}</th>
                                        <th>{{ $t('appUser.table.role') }}</th>
                                        <th>{{ $t('appUser.table.services') }}</th>
                                        <th class="text-end">{{ $t('appUser.table.actions') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="m in item.mappings" :key="m.id">
                                        <td>{{ m.app_name }}</td>
                                        <td>
                                            <v-chip v-if="m.use_case_id === ADMIN_ROLE" color="error" size="small">
                                                {{ $t('appUser.role.admin') }}
                                            </v-chip>
                                            <v-chip v-else-if="m.use_case_id === APP_ADMIN_ROLE" color="warning"
                                                     size="small">
                                                {{ $t('appUser.role.app_admin') }}
                                            </v-chip>
                                            <v-chip v-else color="info" size="small" variant="outlined">
                                                {{ $t('appUser.role.user') }}
                                            </v-chip>
                                        </td>
                                        <td>
                                            <template v-if="m.services && m.services.length > 0">
                                                <v-chip v-for="s in m.services" :key="s" size="x-small" class="mr-1"
                                                        variant="outlined">
                                                    {{ $t('appMgmt.services.' + s) }}
                                                </v-chip>
                                            </template>
                                            <span v-else class="text-caption text-medium-emphasis">
                                                {{ $t('appUser.services.all') }}
                                            </span>
                                        </td>
                                        <td class="text-end">
                                            <v-tooltip location="top">
                                                <template #activator="{ props }">
                                                    <v-btn v-bind="props" icon size="small" variant="text"
                                                           color="kpmgPrimary-900"
                                                           @click="openEdit(m, item)">
                                                        <v-icon>mdi-pencil</v-icon>
                                                    </v-btn>
                                                </template>
                                                {{ $t('appUser.editTooltip') }}
                                            </v-tooltip>
                                            <v-tooltip location="top">
                                                <template #activator="{ props }">
                                                    <v-btn v-bind="props" icon size="small" variant="text"
                                                           color="error"
                                                           @click="openDelete(m)">
                                                        <v-icon>mdi-delete</v-icon>
                                                    </v-btn>
                                                </template>
                                                {{ $t('appUser.deleteTooltip') }}
                                            </v-tooltip>
                                            <v-tooltip v-if="hasAppChat(m)" location="top">
                                                <template #activator="{ props }">
                                                    <v-btn v-bind="props" icon size="small" variant="text"
                                                           color="primary"
                                                           :loading="resendingId === m.id"
                                                           @click="onResendInvite(m)">
                                                        <v-icon>mdi-email-fast</v-icon>
                                                    </v-btn>
                                                </template>
                                                {{ $t('appUser.resendInviteTooltip') }}
                                            </v-tooltip>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </td>
                    </tr>
                </template>

                <template #no-data>
                    <div class="text-center pa-4 text-medium-emphasis">
                        {{ $t('appUser.noData') }}
                    </div>
                </template>
            </v-data-table>
        </div>

        <!-- Create Dialog -->
        <v-dialog v-model="createDialog" max-width="800" persistent>
            <v-card>
                <v-card-title>{{ $t('appUser.createTitle') }}</v-card-title>
                <v-card-text>
                    <v-row>
                        <application-picker
                            v-model="createForm.app_id"
                            prepend-icon="mdi-application"
                            :hint="$t('appUser.app_id.hint')"
                            :label="$t('appUser.app_id.lbl')"
                        />
                        <user-picker
                            v-model="createForm.user_id"
                            prepend-icon="mdi-account-key"
                            :hint="$t('appUser.user_id.hint')"
                            :label="$t('appUser.user_id.lbl')"
                        />
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="createForm.use_case_id"
                                :items="roleOptions"
                                item-title="title"
                                item-value="value"
                                :label="$t('appUser.role.lbl')"
                                :hint="$t('appUser.role.hint')"
                                persistent-hint
                                clearable
                                prepend-icon="mdi-shield-account"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="createForm.services"
                                :items="serviceOptions"
                                item-title="title"
                                item-value="value"
                                :label="$t('appUser.services.lbl')"
                                :hint="$t('appUser.services.hint')"
                                persistent-hint
                                multiple
                                chips
                                closable-chips
                                clearable
                                prepend-icon="mdi-view-dashboard"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" @click="createDialog = false">{{ $t('cancel') }}</v-btn>
                    <v-btn color="primary" variant="elevated" :loading="saving" @click="doCreate">
                        {{ $t('save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Edit Dialog -->
        <v-dialog v-model="editDialog" max-width="800" persistent>
            <v-card>
                <v-card-title>{{ $t('appUser.editTitle') }}</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field
                                :model-value="editUsername"
                                disabled
                                prepend-icon="mdi-account"
                                :label="$t('appUser.user_id.lbl')"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                :model-value="editAppName"
                                disabled
                                prepend-icon="mdi-application"
                                :label="$t('appUser.app_id.lbl')"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="editForm.use_case_id"
                                :items="roleOptions"
                                item-title="title"
                                item-value="value"
                                :label="$t('appUser.role.lbl')"
                                :hint="$t('appUser.role.hint')"
                                persistent-hint
                                clearable
                                prepend-icon="mdi-shield-account"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="editForm.services"
                                :items="serviceOptions"
                                item-title="title"
                                item-value="value"
                                :label="$t('appUser.services.lbl')"
                                :hint="$t('appUser.services.hint')"
                                persistent-hint
                                multiple
                                chips
                                closable-chips
                                clearable
                                prepend-icon="mdi-view-dashboard"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" @click="editDialog = false">{{ $t('cancel') }}</v-btn>
                    <v-btn color="primary" variant="elevated" :loading="saving" @click="doEdit">
                        {{ $t('save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Dialog -->
        <v-dialog v-model="deleteDialog" max-width="500">
            <v-card>
                <v-card-title>{{ $t('appUser.deleteTitle') }}</v-card-title>
                <v-card-text>{{ $t('appUser.deleteText') }}</v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" @click="deleteDialog = false">{{ $t('cancel') }}</v-btn>
                    <v-btn color="error" variant="elevated" :loading="deleting" @click="doDelete">
                        {{ $t('remove') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <view-side-panel/>
    </div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, ref} from "vue"
import {useI18n} from "vue-i18n";
import axios from "axios";
import {useApiCredentials} from "@/composables/useApiCredentials";
import {useAuthorizationStore} from "@/stores/AuthorizationStore/AuthorizationStore";
import {useKMessageStore} from "@/components/dds/KMessageStore/KMessageStore";
import {ADMIN_ROLE, APP_ADMIN_ROLE} from "@/composables/useAppConst";
import ApplicationPicker from "@/components/picker/ApplicationPicker.vue";
import UserPicker from "@/components/picker/UserPicker.vue";
import ViewSidePanel from "@/components/ViewSidePanel.vue";

interface MappingItem {
    id: number
    app_id: number
    app_name: string
    use_case_id: number | null
    services: string[] | null
}

interface GroupedUser {
    user_id: number
    username: string
    display_name: string | null
    first_name: string | null
    last_name: string | null
    mappings: MappingItem[]
}

export default defineComponent({
    components: {
        ApplicationPicker,
        UserPicker,
        ViewSidePanel,
    },
    setup() {
        const {t} = useI18n();
        const {guid, portalBaseUrl, axiosBearerHeader} = useApiCredentials();
        const authStore = useAuthorizationStore();
        const messageStore = useKMessageStore();

        const loading = ref(true)
        const groupedUsers = ref<GroupedUser[]>([])
        const expanded = ref<string[]>([])
        const search = ref('')
        const saving = ref(false)
        const deleting = ref(false)
        const resendingId = ref<number | null>(null)

        // Dialogs
        const createDialog = ref(false)
        const editDialog = ref(false)
        const deleteDialog = ref(false)

        // Create form
        const createForm = ref({
            app_id: 1,
            user_id: 1,
            use_case_id: null as number | null,
            services: null as string[] | null,
        })

        // Edit form
        const editForm = ref({
            use_case_id: null as number | null,
            services: null as string[] | null,
        })
        const editMappingId = ref<number | null>(null)
        const editUsername = ref('')
        const editAppName = ref('')

        // Delete target
        const deleteMappingId = ref<number | null>(null)

        const isGlobalAdmin = computed(() => authStore.roles.includes(ADMIN_ROLE))

        const baseUrl = computed(() =>
            `${portalBaseUrl}/${guid.value}/application/user`
        )

        const roleOptions = computed(() => {
            const options = [
                {title: t('appUser.role.user'), value: null},
                {title: t('appUser.role.app_admin'), value: APP_ADMIN_ROLE},
            ]
            if (isGlobalAdmin.value) {
                options.push({title: t('appUser.role.admin'), value: ADMIN_ROLE})
            }
            return options
        })

        const serviceOptions = computed(() => [
            {title: t('appMgmt.services.agent'), value: 'agent'},
            {title: t('appMgmt.services.workflow'), value: 'workflow'},
            {title: t('appMgmt.services.workflow_assistant'), value: 'workflow_assistant'},
            {title: t('appMgmt.services.guardrails'), value: 'guardrails'},
            {title: t('appMgmt.services.knowledge_base'), value: 'knowledge_base'},
            {title: t('appMgmt.services.developer_api'), value: 'developer_api'},
            {title: t('appMgmt.services.app_chat'), value: 'app_chat'},
            {title: t('appMgmt.services.agentic_workflows'), value: 'agentic_workflows'},
        ])

        const userHeaders = computed(() => [
            {title: t('appUser.table.username'), align: 'start' as const, sortable: true, key: 'username'},
            {title: t('appUser.table.name'), align: 'start' as const, sortable: true, key: 'display_name'},
            {title: t('appUser.table.mappings'), align: 'start' as const, sortable: true, key: 'mappings'},
        ])

        const filteredUsers = computed(() => {
            if (!search.value) return groupedUsers.value
            const q = search.value.toLowerCase()
            return groupedUsers.value.filter(u =>
                u.username.toLowerCase().includes(q) ||
                (u.display_name && u.display_name.toLowerCase().includes(q)) ||
                (u.first_name && u.first_name.toLowerCase().includes(q)) ||
                (u.last_name && u.last_name.toLowerCase().includes(q))
            )
        })

        function fullName(user: GroupedUser): string {
            if (user.first_name && user.last_name) {
                return `${user.first_name} ${user.last_name}`
            }
            return user.display_name || '–'
        }

        async function loadGroupedUsers() {
            try {
                const {data} = await axios.get<GroupedUser[]>(
                    `${baseUrl.value}/grouped`,
                    axiosBearerHeader.value,
                )
                groupedUsers.value = data
            } catch {
                messageStore.push("error", t('appUser.loadError'))
                groupedUsers.value = []
            } finally {
                loading.value = false
            }
        }

        function openCreate() {
            createForm.value = {app_id: 1, user_id: 1, use_case_id: null, services: null}
            createDialog.value = true
        }

        async function doCreate() {
            saving.value = true
            try {
                await axios.post(
                    baseUrl.value,
                    createForm.value,
                    axiosBearerHeader.value,
                )
                messageStore.push("success", t('appUser.createSuccess'))
                createDialog.value = false
                await loadGroupedUsers()
            } catch {
                messageStore.push("error", t('appUser.createError'))
            } finally {
                saving.value = false
            }
        }

        function openEdit(mapping: MappingItem, user: GroupedUser) {
            editMappingId.value = mapping.id
            editUsername.value = user.username
            editAppName.value = mapping.app_name
            editForm.value = {
                use_case_id: mapping.use_case_id,
                services: mapping.services ? [...mapping.services] : null,
            }
            editDialog.value = true
        }

        async function doEdit() {
            if (!editMappingId.value) return
            saving.value = true
            try {
                await axios.put(
                    `${baseUrl.value}/${editMappingId.value}`,
                    editForm.value,
                    axiosBearerHeader.value,
                )
                messageStore.push("success", t('appUser.editSuccess'))
                editDialog.value = false
                await loadGroupedUsers()
            } catch {
                messageStore.push("error", t('appUser.editError'))
            } finally {
                saving.value = false
            }
        }

        function openDelete(mapping: MappingItem) {
            deleteMappingId.value = mapping.id
            deleteDialog.value = true
        }

        async function doDelete() {
            if (!deleteMappingId.value) return
            deleting.value = true
            try {
                await axios.delete(
                    `${baseUrl.value}/${deleteMappingId.value}`,
                    axiosBearerHeader.value,
                )
                messageStore.push("success", t('appUser.deleteSuccess'))
                deleteDialog.value = false
                await loadGroupedUsers()
            } catch {
                messageStore.push("error", t('appUser.deleteError'))
            } finally {
                deleting.value = false
            }
        }

        function hasAppChat(mapping: MappingItem): boolean {
            return !!(mapping.services && mapping.services.includes('app_chat'))
        }

        async function onResendInvite(mapping: MappingItem) {
            resendingId.value = mapping.id
            try {
                await axios.post(
                    `${baseUrl.value}/${mapping.id}/resend-invite`,
                    null,
                    axiosBearerHeader.value,
                )
                messageStore.push("success", t('appUser.resendInviteSuccess'))
            } catch {
                messageStore.push("error", t('appUser.resendInvite'))
            } finally {
                resendingId.value = null
            }
        }

        onMounted(() => {
            loadGroupedUsers()
        })

        return {
            loading,
            groupedUsers,
            expanded,
            search,
            saving,
            deleting,
            resendingId,
            userHeaders,
            filteredUsers,
            roleOptions,
            serviceOptions,
            createDialog,
            editDialog,
            deleteDialog,
            createForm,
            editForm,
            editMappingId,
            editUsername,
            editAppName,
            openCreate,
            doCreate,
            openEdit,
            doEdit,
            openDelete,
            doDelete,
            hasAppChat,
            onResendInvite,
            fullName,
            ADMIN_ROLE,
            APP_ADMIN_ROLE,
        }
    },
})
</script>
